"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.QuantitySelector = QuantitySelector;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const prop_types_1 = require("prop-types");
const classnames_1 = __importDefault(require("classnames"));
const pattern_library_components_icon_1 = require("@saatva-bits/pattern-library.components.icon");
const QuantitySelector_module_scss_1 = __importDefault(require("./QuantitySelector.module.scss"));
const selection_hooks_1 = require("../../selection.hooks");
/**
 * Render all selectors available for a product
 * @param {Object} props
 * @param {string} props.productCode - The productCode of the product whose quantity will be managed by the QuantitySelector component
 * @param {string} props.className - A custom class name for the QuantitySelector container
 * @param {string} props.dataSelector - Add a custom prefix to the data-selector attributes for the QuantitySelector elements
 * @param {boolean} props.displayIfOutOfStock - if true, display the quantity selector even if the variant is out of stock
 * @param {number} props.maxQuantityOverride - Override the product's maxQuantity attribute
 * @returns {import('react').ReactNode}
 */
function QuantitySelector({ productCode, className, dataSelector = 'quantitySelector', displayIfOutOfStock = true, maxQuantityOverride }) {
    const { quantity, inStock } = (0, selection_hooks_1.useProductState)(productCode);
    const [count, setCount] = (0, react_1.useState)(quantity);
    let { maxQuantity } = (0, selection_hooks_1.useProductData)(productCode);
    maxQuantity = maxQuantityOverride || maxQuantity;
    const maxlength = maxQuantity.toString().length;
    const updateProductAttributes = (0, selection_hooks_1.useUpdateProductAttributes)();
    /**
     * The cart page can initialize the Detail Modal with a quantity other than 1
     * This useEffect is responsible for updating the UI when the product state
     * changes from the cart page
     */
    (0, react_1.useEffect)(() => {
        setCount(quantity);
    }, [quantity]);
    // if max quantity is 1, don't render quantity selector, override behavior with maxQuantityOverride
    // if out of stock, don't render quantity selector, override with displayIfOutOfStock
    if (maxQuantity === 1 || (!displayIfOutOfStock && !inStock)) {
        return null;
    }
    const validateInput = (input) => {
        const inputNumber = parseInt(input, 10);
        return !Number.isNaN(inputNumber) && inputNumber >= 1 && inputNumber <= maxQuantity;
    };
    const handleIncrement = () => {
        const updatedQuantity = Number(quantity) + 1;
        if (validateInput(updatedQuantity)) {
            updateProductAttributes(productCode, { quantity: updatedQuantity.toString() });
            setCount(updatedQuantity);
        }
    };
    const handleDecrement = () => {
        const updatedQuantity = Number(quantity) - 1;
        if (validateInput(updatedQuantity)) {
            updateProductAttributes(productCode, { quantity: updatedQuantity.toString() });
            setCount(updatedQuantity);
        }
    };
    // set the visible text, but do not update quantity until after the user has moved past the input
    // this allows the user to interact with the text input like any other input
    const handleChange = (e) => {
        e.preventDefault();
        setCount(e.target.value);
    };
    // validate and set the input
    // if the value entered is valid, set it
    // if value entered is higher than the maxQuantity, set it to the maxQuantity
    // If the value is invalid, set it to 1
    const handleBlur = (e) => {
        // parse int will remove leading 0's and trim whitespace
        const updatedQuantity = parseInt(e.target.value, 10);
        if (validateInput(updatedQuantity)) {
            updateProductAttributes(productCode, { quantity: updatedQuantity.toString() });
            setCount(updatedQuantity);
        }
        else if (!Number.isNaN(updatedQuantity) && Number(updatedQuantity) > Number(maxQuantity)) {
            updateProductAttributes(productCode, { quantity: maxQuantity.toString() });
            setCount(`${maxQuantity.toString()}`);
        }
        else {
            updateProductAttributes(productCode, { quantity: '1' });
            setCount('1');
        }
    };
    const containerClasses = (0, classnames_1.default)(QuantitySelector_module_scss_1.default.container, className);
    const leftButtonClasses = (0, classnames_1.default)(QuantitySelector_module_scss_1.default.button, QuantitySelector_module_scss_1.default.leftButton);
    const rightButtonClasses = (0, classnames_1.default)(QuantitySelector_module_scss_1.default.button, QuantitySelector_module_scss_1.default.rightButton);
    return ((0, jsx_runtime_1.jsxs)("div", { "data-selector": `${dataSelector}_container`, className: containerClasses, children: [(0, jsx_runtime_1.jsx)("button", { type: 'button', className: leftButtonClasses, onClick: () => handleDecrement(), "data-selector": `${dataSelector}_decreaseBtn`, disabled: quantity <= 1, children: (0, jsx_runtime_1.jsx)(pattern_library_components_icon_1.Icon, { alt: 'decrease quantity by 1', description: "decrease quantity by 1", name: 'less', titleId: "decreaseQty" }) }), (0, jsx_runtime_1.jsx)("input", { type: 'text', maxLength: maxlength, className: QuantitySelector_module_scss_1.default.input, "data-selector": `${dataSelector}_input`, value: count, onChange: (e) => handleChange(e), onBlur: handleBlur }), (0, jsx_runtime_1.jsx)("button", { type: 'button', className: rightButtonClasses, onClick: () => handleIncrement(), "data-selector": `${dataSelector}_increaseBtn`, disabled: quantity >= maxQuantity, children: (0, jsx_runtime_1.jsx)(pattern_library_components_icon_1.Icon, { alt: 'increase quantity by 1', description: "increase quantity by 1", name: 'more', titleId: "increaseQty" }) })] }));
}
QuantitySelector.propTypes = {
    /** The productCode of the product whose quantity will be managed by the QuantitySelector component */
    productCode: prop_types_1.string.isRequired,
    /** A custom class name for the QuantitySelector container */
    className: prop_types_1.string,
    /** Add a custom prefix to the data-selector attributes for the QuantitySelector elements */
    dataSelector: prop_types_1.string,
    /** Display the quantity selector if the variant is out of stock */
    displayIfOutOfStock: prop_types_1.bool,
    /** Override the product's maxQuantity */
    maxQuantityOverride: prop_types_1.number
};
exports.default = QuantitySelector;
//# sourceMappingURL=QuantitySelector.js.map